import { Column } from "interfaces/LightThemeTable";
import React, { ChangeEvent } from "react";
import Loader from "./Loader";
import NoDataAvailable from "./NoDataLightTheme";

import { Box, Grid } from "@material-ui/core";
import DESC_ICON from "assets/img/arrow-down.svg";
import ASC_ICON from "assets/img/arrow-up.svg";

/**
 * Props for the server-side pagination table.
 *
 * Unlike LightThemeTable, this component does NOT slice data internally.
 * The parent is responsible for fetching exactly one page of records from
 * the server and passing them as `data`.  Pagination state (currentPage,
 * pageSize) is fully controlled by the parent via props; the component
 * notifies the parent of user interactions through `onPageChange`.
 */
export interface ServerPaginatedTableProps<T> {
  data: T[];
  columns: Column<T>[];
  /** 1-indexed current page number (controlled by parent) */
  currentPage: number;
  /** Number of rows per page (controlled by parent) */
  pageSize: number;
  /** Total number of pages returned by the server (pageCount from API) */
  totalPages: number;
  pageSizeOptions?: number[];
  isLoading?: boolean;
  /**
   * Called when the user requests a different page or page size.
   * `page` is 1-indexed so the parent must subtract 1 before sending to the
   * server (server uses 0-indexed `page` query param).
   */
  onPageChange: (page: number, pageSize: number) => void;
  onClickOnRow?: (row: T) => void;
  onSortChange?: (field: string, order: "asc" | "desc") => void;
  tableHeight?: string | number;
  showPagination?: boolean;
}

interface ServerPaginatedTableState {
  sortField: string;
  sortOrder: "asc" | "desc";
  gotoPage: string;
}

export class LightThemeTableWithPagination<
  T extends { id?: string | number; [key: string]: any }
> extends React.Component<ServerPaginatedTableProps<T>, ServerPaginatedTableState> {
  static defaultProps = {
    pageSizeOptions: [10, 25, 50, 100],
  };

  constructor(props: ServerPaginatedTableProps<T>) {
    super(props);
    this.state = {
      sortField: "",
      sortOrder: "asc",
      gotoPage: "",
    };
  }

  /* ─────────────────────────── Sorting ──────────────────────────────────── */

  handleSort = (field: string) => {
    // Toggle order if the same field is clicked again.
    let order: "asc" | "desc" = "asc";
    if (field === this.state.sortField) {
      order = this.state.sortOrder === "asc" ? "desc" : "asc";
    }
    this.setState({ sortField: field, sortOrder: order }, () => {
      // When onSortChange is provided the parent is responsible for
      // fetching new data from the server — skip client-side sort.
      this.props.onSortChange?.(field, order);
    });
  };

  /**
   * Client-side sort used only when onSortChange is NOT wired.
   * When onSortChange IS provided the server already returned sorted data,
   * so we render data as-is.
   */
  sortData = (rows: T[]): T[] => {
    if (this.props.onSortChange) return rows; // server handles sort
    const { sortField, sortOrder } = this.state;
    if (!sortField) return rows;
    const isStoreNumberField = /store.*(number|num|no|id)$/i.test(sortField);
    return [...rows].sort((a, b) => {
      let x = a[sortField];
      let y = b[sortField];

      if (isStoreNumberField) {
        const sx = String(x ?? "").trim();
        const sy = String(y ?? "").trim();
        const nx = /^\d+$/.test(sx);
        const ny = /^\d+$/.test(sy);
        const result = nx && ny ? Number(sx) - Number(sy) : nx !== ny ? nx ? -1 : 1 : sx.localeCompare(sy, undefined, { numeric: true, sensitivity: "base" });
        return sortOrder === "asc" ? result : -result;
      }

      if (typeof x === "string") x = x.toLowerCase();
      if (typeof y === "string") y = y.toLowerCase();
      if (x < y) return sortOrder === "asc" ? -1 : 1;
      if (x > y) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  /* ─────────────────────────── Pagination handlers ──────────────────────── */

  handlePageChange = (page: number) => {
    const { totalPages, pageSize } = this.props;
    const clamped = Math.max(1, Math.min(page, totalPages));
    this.props.onPageChange(clamped, pageSize);
  };

  handlePageSizeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value, 10);
    // Always go back to page 1 when the page size changes
    this.props.onPageChange(1, newSize);
  };

  handleGotoPage = () => {
    const pageNumber = parseInt(this.state.gotoPage, 10);
    if (!isNaN(pageNumber)) {
      this.handlePageChange(pageNumber);
      this.setState({ gotoPage: "" });
    }
  };

  /* ─────────────────────────── Page number list ──────────────────────────── */

  getPageNumbers = (): (number | string)[] => {
    const { totalPages, currentPage } = this.props;
    const pages: (number | string)[] = [];
    if (totalPages <= 3) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, 2);
      if (currentPage >= 3 && currentPage < totalPages - 1) {
        pages.push("...", currentPage, "...");
      } else if (currentPage >= totalPages - 1) {
        pages.push("...", totalPages - 1);
      } else {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  /* ─────────────────────────── Render ───────────────────────────────────── */

  render() {
    const {
      columns,
      data,
      isLoading,
      onClickOnRow,
      pageSizeOptions,
      currentPage,
      pageSize,
      totalPages,
      tableHeight,
      showPagination = true,
    } = this.props;
    const { sortField, sortOrder, gotoPage } = this.state;

    const bodyWrapperStyle: React.CSSProperties = tableHeight
      ? { minHeight: "225px", background: "white", overflowY: "auto", overflowX: "hidden" }
      : { overflowY: "auto", minHeight: "225px", background: "white" };

    const displayData = this.sortData(data ?? []);

    return (
      <div style={{ width: "100%" }}>
        <div className="api-table-body-wrapper" style={bodyWrapperStyle}>
          <table
            style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}
          >
            <thead style={{ backgroundColor: "#F9FAFB" }}>
              <tr>
                {columns.map((col) => {
                  const fieldKey = String(col.field);
                  const isSorted = sortField === fieldKey;

                  return (
                    <th
                      key={fieldKey}
                      style={{
                        minWidth: col.minWidth,
                        maxWidth: col.maxWidth,
                        textAlign: "left",
                        padding: "12px 10px",
                        borderBottom: "2px solid #E3E6EA",
                        fontWeight: "bold",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                        position: "relative",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                        {col.header && String(col.header).trim() !== "" && (
                          <span
                            style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                            onClick={() => this.handleSort(fieldKey)}
                          >
                            {col.header}
                            <img
                              src={
                                isSorted
                                  ? sortOrder === "asc"
                                    ? ASC_ICON
                                    : DESC_ICON
                                  : ASC_ICON
                              }
                              style={{
                                width: 10,
                                opacity: isSorted ? 1 : 0.4,
                                marginLeft: 6,
                                flexShrink: 0,
                              }}
                              alt="sort"
                            />
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody style={{ width: "100%", height: "100%" }}>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <Loader />
                  </td>
                </tr>
              ) : displayData.length > 0 ? (
                displayData.map((row, idx) => (
                  <tr
                    key={row.id ?? idx}
                    onClick={() => onClickOnRow?.(row)}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#FFFFFF" : "#FEFCE8",
                      cursor: onClickOnRow ? "pointer" : "default",
                    }}
                  >
                    {columns.map((col) => (
                      <td
                        key={String(col.header)}
                        style={{
                          padding: "5px 12px",
                          maxWidth: col.maxWidth,
                          minWidth: col.minWidth,
                          border: "none",
                          fontSize: "12px",
                          whiteSpace: "nowrap",
                          borderBottom: "2px solid #FDF19EC2",
                        }}
                      >
                        {col.formatter
                          ? col.formatter(row[col.field as string], row, idx)
                          : row[col.field as string] ?? "-"}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} style={{ padding: 0 }}>
                    <NoDataAvailable />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showPagination && totalPages > 0 && (
          <Grid container spacing={2} style={{ padding: "16px 8px" }}>
            {/* Page size selector */}
            <Grid xs={12} md={4} spacing={1}>
              <label
                style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}
              >
                Rows per page:
                <select
                  value={pageSize}
                  onChange={this.handlePageSizeChange}
                  style={{
                    width: 60,
                    padding: "4px 6px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    textAlign: "center",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  {pageSizeOptions?.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            </Grid>

            {/* Page number buttons */}
            <Grid xs={12} md={4}>
              <Box
                flex="1 1 auto"
                display="flex"
                justifyContent={{ xs: "flex-start", md: "center" }}
                alignItems="center"
              >
                <span
                  onClick={() => this.handlePageChange(currentPage - 1)}
                  style={{
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    color: currentPage === 1 ? "#ccc" : "#000",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  &lt;
                </span>

                {this.getPageNumbers().map((p, idx) =>
                  typeof p === "number" ? (
                    <span
                      key={idx}
                      onClick={() => this.handlePageChange(p)}
                      style={{
                        cursor: "pointer",
                        padding: "4px 8px",
                        fontSize: "12px",
                        borderBottom:
                          currentPage === p ? "2px solid #E5B42F" : "2px solid transparent",
                      }}
                    >
                      {p}
                    </span>
                  ) : (
                    <span
                      key={idx}
                      style={{ padding: "4px 8px", fontSize: "12px", cursor: "default" }}
                    >
                      {p}
                    </span>
                  )
                )}

                <span
                  onClick={() => this.handlePageChange(currentPage + 1)}
                  style={{
                    cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                    color: currentPage === totalPages ? "#ccc" : "#000",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  &gt;
                </span>
              </Box>
            </Grid>

            {/* Go to page */}
            <Grid xs={12} md={4}>
              <Box
                style={{ gap: "6px" }}
                display="flex"
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", md: "flex-end" }}
                alignItems="center"
                fontSize={12}
              >
                <span>Go To</span>
                <input
                  type="text"
                  value={gotoPage}
                  onChange={(e) => this.setState({ gotoPage: e.target.value })}
                  placeholder="Page"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") this.handleGotoPage();
                  }}
                  style={{
                    width: 60,
                    padding: "4px 6px",
                    border: "1px solid #ccc",
                    borderRadius: 4,
                    textAlign: "center",
                    fontSize: "12px",
                  }}
                />
                <button
                  onClick={this.handleGotoPage}
                  style={{
                    backgroundColor: "#E5B42F",
                    border: "none",
                    padding: "5px 12px",
                    borderRadius: 4,
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  Go
                </button>
                <span style={{ fontSize: "12px", color: "#6B7280", marginLeft: 4 }}>
                  of {totalPages}
                </span>
              </Box>
            </Grid>
          </Grid>
        )}
      </div>
    );
  }
}
